import { useCallback, useEffect, useRef, useState } from 'react';
import { Message } from '../model';

interface UseChatScrollProps {
  loadMore: () => void;
  enabled?: boolean;
  messages: Message[];
  hasMoreMessages: boolean;
  isLoadingMore: boolean;
}

export const useChatScroll = ({
  loadMore,
  enabled = true,
  messages,
  hasMoreMessages,
  isLoadingMore,
}: UseChatScrollProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);

  const [showScrollButton, setShowScrollButton] = useState(false);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [hasNewMessages, setHasNewMessages] = useState(false);

  const isAtBottomRef = useRef(true);
  const tickingRef = useRef(false);
  const targetScrollTopRef = useRef<number | null>(null);
  const lastSeenMessageIdRef = useRef<string | null>(null);

  useEffect(() => {
    isLoadingRef.current = isLoadingMore;
  }, [isLoadingMore]);

  const checkIsAtBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return true;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    return Math.abs(distanceFromBottom) < 50;
  }, []);

  const checkShouldShowScrollButton = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return false;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    return distanceFromBottom >= 800;
  }, []);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingRef.current || !hasMoreMessages) return;
    isLoadingRef.current = true;

    const container = scrollContainerRef.current;
    if (!container) return;

    const currentScrollTop = container.scrollTop;
    const currentScrollHeight = container.scrollHeight;

    loadMore();

    requestAnimationFrame(() => {
      if (container) {
        const newScrollHeight = container.scrollHeight;
        const heightDifference = newScrollHeight - currentScrollHeight;
        targetScrollTopRef.current = currentScrollTop;
        container.scrollTop = currentScrollTop + heightDifference;
      }
    });
  }, [loadMore, hasMoreMessages]);

  const handleScroll = useCallback(async () => {
    if (isLoadingRef.current) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollTop = container.scrollTop;
    const loadThreshold = 100;

    if (
      targetScrollTopRef.current !== null &&
      Math.abs(scrollTop - targetScrollTopRef.current) < 10
    ) {
      targetScrollTopRef.current = null;
      return;
    }

    if (
      scrollTop <= loadThreshold &&
      hasMoreMessages &&
      !isLoadingRef.current
    ) {
      handleLoadMore();
    }

    const isAtBottom = checkIsAtBottom();

    isAtBottomRef.current = isAtBottom;
    setShowScrollButton(checkShouldShowScrollButton());

    if (isAtBottom) {
      setHasNewMessages(false);
      setNewMessagesCount(0);

      if (messages.length > 0) {
        lastSeenMessageIdRef.current = messages[messages.length - 1]
          .id as string;
      }
    }
  }, [
    handleLoadMore,
    hasMoreMessages,
    checkIsAtBottom,
    checkShouldShowScrollButton,
    messages,
  ]);

  const throttledScroll = useCallback(() => {
    return () => {
      if (!tickingRef.current) {
        requestAnimationFrame(() => {
          handleScroll();
          tickingRef.current = false;
        });
        tickingRef.current = true;
      }
    };
  }, [handleScroll])();

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !enabled) return;

    container.addEventListener('scroll', throttledScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', throttledScroll);
    };
  }, [enabled, throttledScroll]);

  useEffect(() => {
    if (!enabled) return;

    if (messages.length === 0) {
      lastSeenMessageIdRef.current = null;
      return;
    }

    if (lastSeenMessageIdRef.current === null && messages.length > 0) {
      lastSeenMessageIdRef.current = messages[messages.length - 1].id as string;
      return;
    }

    if (lastSeenMessageIdRef.current && !isAtBottomRef.current) {
      const lastSeenIndex = messages.findIndex(
        (msg) => msg.id === lastSeenMessageIdRef.current
      );

      if (lastSeenIndex >= 0) {
        const newMessagesAfterLastSeen = messages.slice(lastSeenIndex + 1);

        if (newMessagesAfterLastSeen.length > 0) {
          setHasNewMessages(true);
          setNewMessagesCount(newMessagesAfterLastSeen.length);
        }
      } else {
        setHasNewMessages(true);
        setNewMessagesCount(messages.length);
      }
    }
  }, [messages, enabled]);

  useEffect(() => {
    if (!enabled) {
      setShowScrollButton(false);
      setHasNewMessages(false);
      setNewMessagesCount(0);
    }
  }, [enabled]);

  return {
    scrollContainerRef,
    isLoadingMore,
    showScrollButton,
    hasNewMessages,
    newMessagesCount,
  };
};
